<script setup lang="ts">
  import { computed } from 'vue';

  import { humanize } from '@stuut/common/src/utils';
  import { Badge } from '@ui/components/badge';
  import type { BadgeVariants } from '@ui/components/badge';

  interface Props {
    badgeSize?: BadgeVariants['size'];
    dashed?: boolean;
    defaultVariant?: string;
    disabled?: boolean;
    dropdown?: boolean;
    entity?: Record<string, any>;
    entityKey?: string;
    label?: string;
    leftIcon?: string;
    outline?: boolean;
    overrideMap?: Record<string, string>;
    rightIcon?: string;
    rounded?: boolean;
    value?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    defaultVariant: 'outline',
  });

  const status = computed(() => statusValue.value || entityValue.value);
  const statusValue = computed(() => props.value);
  const badgeVariant = computed(() => getVariant(status.value));

  const statusBadgeMap = computed(() => ({
    archived: 'outline',
    closed: 'secondary',
    complete: 'success',
    completed: 'success',
    broken_promise: 'destructive',
    draft: 'warning',
    failed: 'destructive',
    needs_review: 'destructive',
    open: 'primary',
    overdue: 'destructive',
    paid: 'success',
    payment_promised: 'success',
    promised: 'success',
    partially_paid: 'warning',
    pending: 'warning',
    pending_sync: 'warning',
    processing: 'secondary',
    proposed: 'agent',
    resolved: 'primary',
    review: 'destructive',
    succeeded: 'success',
    void: 'secondary',
    ...(props.overrideMap || {}),
  }));

  const entityValue = computed(() => {
    const key = props.entityKey || 'status';
    return props.entity?.[key];
  });

  const getVariant = (status?: string): BadgeVariants['variant'] | null => {
    const value = status?.trim()?.toLowerCase() || '';

    return (statusBadgeMap.value[value] as BadgeVariants['variant']) || null;
  };
</script>

<template>
  <Badge
    class="flex items-center gap-x-1.5 capitalize select-none"
    :class="[{ 'cursor-pointer': dropdown }]"
    :variant="
      badgeVariant || (defaultVariant ? (defaultVariant as BadgeVariants['variant']) : undefined)
    "
    :rounded="rounded"
    :dashed="dashed"
    :disabled="disabled"
    :outline="outline"
    :size="badgeSize"
  >
    <i v-if="leftIcon" :class="leftIcon" />
    <slot v-if="$slots.before_status" name="before_status" />

    <template v-if="$slots.label">
      <slot name="default" />
    </template>

    <span v-else>{{ label || humanize(value) || humanize(entityValue) }}</span>

    <slot v-if="$slots.after_status" name="after_status" />
    <i v-if="rightIcon" :class="rightIcon" />
    <i v-if="dropdown" class="fas fa-chevron-down" />
  </Badge>
</template>

<style scoped></style>
